
import {
  FinanceQuote,
  FinanceQuoteDeferredPayment,
  GenericGrid, getEnumForValue,
  getOptionsFromEnumAndLoanClass
} from '@amsanalytics/react-shared-library';
import React from 'react';
import styles from './_finance-deferred-down-editor.module.scss';
import { v4 } from "uuid";
import { hasValue } from "@amsanalytics/react-shared-library";
import FinanceDeferredDownEditorDialog, { DeferredDownInfo } from "./FinanceDeferredDownEditorDialog/FinanceDeferredDownEditorDialog";
import Checkbox from "@mui/material/Checkbox";
import FormControlLabel from "@mui/material/FormControlLabel";
import Tooltip from "@mui/material/Tooltip";
import produce from "immer";
import { Card, CardHeader, CardTitle } from '@progress/kendo-react-layout';
import AddIcon from "@mui/icons-material/Add";
import { IconButton } from '@mui/material';
import {IConfiguration} from "../../../../Data/ConfigurationData";
import {ILookupItems} from "../../../../Interfaces/ILookupItems";
import {IApplication} from "../../../../Interfaces/Application/IApplication";
import {IQuotePayType} from "../../../../Interfaces/Common/IQuotePayType";
import {
  addDaysBasedOnPaymentFrequency,
  getFinanceDeferredDownPaymentDates,
  getSecondFinanceLoanPaymentDate,
  startOfDay
} from "../../../../utils/LoanCalcUtils";
import InformationDialog from "../../../Dialogs/InformationDialog";
import GenericItemEditorDialog, {
  GenericEditorActionType,
  IGenericItemEditorError,
  IGenericItemFieldInfo
} from "../../GenericItemEditorDialog/GenericItemEditorDialog";
import {ComboBox, ComboBoxChangeEvent} from "@progress/kendo-react-dropdowns";
import QuoterError from "../../QuoterError/QuoterError";
import {IRuleViolation} from "../../../../Interfaces/Application/IApplicationQuote";
import {getFinanceQuoteErrorMessages} from "../../../../utils/quoteUtils";

export interface FinanceDeferredDownEditorProps {
  selectedFinanceQuote: FinanceQuote;
  configuration: IConfiguration;
  financeQuoteFieldIsDisabled: (fieldName: string) => boolean;
  lookups: ILookupItems;
  onChangeSingleField: <K extends keyof FinanceQuote>(fieldName: K, value: FinanceQuote[K], financeQuoteId?: string) => void;
  currentApp: IApplication;
  className?: string;
  applicationFinanceErrors: IRuleViolation[];
  quotePayTypes: IQuotePayType[];
  openDialog: (options: { children: React.ReactNode }) => void;
  closeDialog: () => void;
  onUpdateApp: (updatedApp: IApplication) => void;
}

export default function FinanceDeferredDownEditor(props: FinanceDeferredDownEditorProps) {
  const { openDialog, closeDialog, quotePayTypes } = props;
  const deferredPayments = [...props.selectedFinanceQuote.financeQuoteDeferredPayments]
    .sort((a, b) =>
      new Date(a.paymentDate!) > new Date(b.paymentDate!) ? 1 : -1
    );
  const quoteWasCreatedAfterCutoverDate = new Date(props.selectedFinanceQuote.createdDate) > new Date(props.configuration.deferredDownEditorCutoverDate);

  const contractDateMin = startOfDay(new Date(props.selectedFinanceQuote.contractDate));

  const editableFieldInfos: IGenericItemFieldInfo[] = [
    { name: "paymentDate", label: "Payment Date", type: "Date", required: true, minDate: contractDateMin },
    { name: "amount", label: "Amount", type: "number", required: true }
  ];

  const maxNumberOfPayments = props.configuration.deferredDownMaxPayments;

  // For the old deferred down payment dialog
  const validateDeferredDownPayment = (deferredPayment: FinanceQuoteDeferredPayment, actionType: GenericEditorActionType): IGenericItemEditorError[] => {
    const errors: IGenericItemEditorError[] = [];

    const secondLoanPaymentDate = getSecondFinanceLoanPaymentDate(new Date(props.selectedFinanceQuote.firstPaymentDate), props.selectedFinanceQuote.paymentFrequency, quotePayTypes);
    const deferredPaymentDate = new Date(deferredPayment.paymentDate!);
    deferredPaymentDate!.setHours(0, 0, 0, 0);

    if (deferredPaymentDate > secondLoanPaymentDate) {
      errors.push({ message: 'Deferred down payments may have specific regulatory requirements. Please consult your compliance officer.', canBeBypassed: true });
    }

    if (hasValue(deferredPayment.amount) && deferredPayment.amount! <= 0) {
      errors.push({ message: "The payment amount has to be greater than 0", canBeBypassed: false });
    }

    const deferredPaymentDates = deferredPayments.map(dp => new Date(dp.paymentDate!));
    const numberOfExistingPaymentDatesWithSameDate = deferredPaymentDates.filter(dpd => dpd.setHours(0, 0, 0, 0) === deferredPaymentDate.setHours(0, 0, 0, 0)).length;
    if ((actionType === "Add" && numberOfExistingPaymentDatesWithSameDate > 0) // There aren't any for that date
      || (actionType === "Update" && numberOfExistingPaymentDatesWithSameDate > 1)) { // There is already one for that date because you're updating it
      errors.push({ message: "The payment date specified is already in use", canBeBypassed: false });
    }

    return errors;
  }

  const handleRemoveSingleDeferredPayment = (deferredPaymentId: string) => {
    // Using this to match on the date for the other quotes
    const deferredPaymentToRemoveFromSelectedQuote =
      props.selectedFinanceQuote.financeQuoteDeferredPayments.find(dp => dp.id === deferredPaymentId);

    if (!deferredPaymentToRemoveFromSelectedQuote) {
      return;
    }

    const updatedApp = produce(props.currentApp, updatedCurrentApp => {
      // Applying the same values to each quote
      updatedCurrentApp.financeQuotes.forEach(financeQuote => {
        // Taking out the deferred payment for the date of the one that was removed on the selected quote
        financeQuote.financeQuoteDeferredPayments = financeQuote.financeQuoteDeferredPayments
          .filter(payment => new Date(payment.paymentDate!).setHours(0, 0, 0, 0) !== new Date(deferredPaymentToRemoveFromSelectedQuote.paymentDate!).setHours(0, 0, 0, 0));
      });
    });
    props.onUpdateApp(updatedApp);

    closeDialog();
  }

  const handleRemoveAllDeferredPayments = () => {
    const updatedApp = produce(props.currentApp, updatedCurrentApp => {
      // Applying the same values to each quote
      updatedCurrentApp.financeQuotes.forEach(financeQuote => {
        // Taking out the deferred payment for the date of the one that was removed on the selected quote
        financeQuote.financeQuoteDeferredPayments = [];
      });
    });
    props.onUpdateApp(updatedApp);

    closeDialog();
  }

  const handleUpdateDeferredPaymentOld = (updatedDeferredPayment: FinanceQuoteDeferredPayment): void => {
    const originalDateFromSelectedQuote = props.selectedFinanceQuote.financeQuoteDeferredPayments.find(dp => dp.id === updatedDeferredPayment.id)?.paymentDate;

    const updatedApp = produce(props.currentApp, updatedCurrentApp => {
      // Applying the same values to each quote
      updatedCurrentApp.financeQuotes.forEach(financeQuoteToUpdate => {
        const deferredPayments: FinanceQuoteDeferredPayment[] = financeQuoteToUpdate.financeQuoteDeferredPayments;
        const matchedDeferredPayment = originalDateFromSelectedQuote !== undefined
          ? deferredPayments.find(dp => dp.paymentDate && new Date(dp.paymentDate).setHours(0, 0, 0, 0) === new Date(originalDateFromSelectedQuote).setHours(0, 0, 0, 0))
          : deferredPayments.find(dp => dp.paymentDate && updatedDeferredPayment.paymentDate && new Date(dp.paymentDate).setHours(0, 0, 0, 0) === new Date(updatedDeferredPayment.paymentDate).setHours(0, 0, 0, 0));
        if (matchedDeferredPayment) {
          // Modifies
          matchedDeferredPayment.paymentDate = updatedDeferredPayment.paymentDate;
          matchedDeferredPayment.amount = updatedDeferredPayment.amount;
        } else {
          // Creates
          deferredPayments.push({
            ...updatedDeferredPayment,
            id: v4(),
            financeQuoteId: financeQuoteToUpdate.id!
          });
        }
      })
    });
    props.onUpdateApp(updatedApp);
    closeDialog();
  }

  const handleUpdateDeferredPaymentNew = (deferredDownInfo: DeferredDownInfo): void => {
    let showDialogAfterUpdate = false;
    let newFirstPaymentDate: Date | undefined = undefined;
    const newDeferredDownPaymentDates = getFinanceDeferredDownPaymentDates(deferredDownInfo.startDate!, deferredDownInfo.paymentFrequency, deferredDownInfo.numberOfPayments, quotePayTypes);

    const updatedApp = produce(props.currentApp, updatedCurrentApp => {
      // Applying the same values to each quote
      updatedCurrentApp.financeQuotes.forEach(financeQuoteToUpdate => {
        financeQuoteToUpdate.financeQuoteDeferredPayments = newDeferredDownPaymentDates.map(paymentDate => {
          return {
            id: v4(),
            financeQuoteId: financeQuoteToUpdate.id!,
            paymentDate: paymentDate,
            amount: deferredDownInfo.paymentAmount,
            paymentFrequency: deferredDownInfo.paymentFrequency
          }
        });
        financeQuoteToUpdate.deferredDownPaymentPaymentFrequency = deferredDownInfo.paymentFrequency;

        // If we are supposed to the tie the deferred down payment dates to the loan's first payment date, update it here 
        if (financeQuoteToUpdate.financeQuoteDeferredPayments.length > 0
          && props.selectedFinanceQuote.tieDeferredDownPaymentsToFirstPaymentDate) {
          const lastDeferredDownPaymentDate = new Date(Math.max(...financeQuoteToUpdate.financeQuoteDeferredPayments.map(dp => new Date(dp.paymentDate!).getTime())))
          financeQuoteToUpdate.firstPaymentDate = addDaysBasedOnPaymentFrequency(
            lastDeferredDownPaymentDate,
            financeQuoteToUpdate.deferredDownPaymentPaymentFrequency,
            quotePayTypes,
            "Add"
          );
          showDialogAfterUpdate = true;
          newFirstPaymentDate = financeQuoteToUpdate.firstPaymentDate;
        }
      })
    });
    props.onUpdateApp(updatedApp);
    closeDialog();

    if (showDialogAfterUpdate && hasValue(newFirstPaymentDate)) {
      openDialog({
        children: (
          <InformationDialog
            title={"First Payment Date Update"}
            body={`After the deferred down payment change, the loan's first payment date has been updated to ${newFirstPaymentDate!.toDateString()}. If you'd wish for the deferred down payment dates to not affect the loan's first payment date and vice versa, uncheck the "Keep first payment date and deferred down payment dates in sync" checkbox.`}
            closeDialog={closeDialog}
          />
        )
      });
    }
  }

  const addOrEditDeferredPayment = (deferredPayment: FinanceQuoteDeferredPayment | undefined) => {
    const buttonText = deferredPayment ? "Update" : "Add";
    const showRemoveButton = !!deferredPayment;
    if (!deferredPayment) {
      deferredPayment = {
        id: v4() // Have to have an ID so that it can be updated, if needed
      }
    }

    openDialog({
      children: (
        quoteWasCreatedAfterCutoverDate
          ?
          <FinanceDeferredDownEditorDialog
            title={"Deferred Down"}
            submitButtonText={buttonText}
            onSubmit={(deferredDownInfo: DeferredDownInfo) => handleUpdateDeferredPaymentNew(deferredDownInfo)}
            closeDialog={closeDialog}
            onRemoveAll={handleRemoveAllDeferredPayments}
            selectedFinanceQuote={props.selectedFinanceQuote}
            lookups={props.lookups}
            maxNumberOfPayments={maxNumberOfPayments}
          />
          :
          <GenericItemEditorDialog
            item={deferredPayment as FinanceQuoteDeferredPayment}
            objectSpecificValidator={validateDeferredDownPayment}
            title={"Deferred Down"}
            editableFieldInfos={editableFieldInfos}
            submitButtonText={buttonText}
            onSubmit={(updatedDeferredPayment: FinanceQuoteDeferredPayment) => handleUpdateDeferredPaymentOld(updatedDeferredPayment)}
            closeDialog={closeDialog}
            onRemove={showRemoveButton ? handleRemoveSingleDeferredPayment : undefined}
          />
      )
    })
  }
  
  const paymentFrequencies = getOptionsFromEnumAndLoanClass(props.lookups.carletonItems, "PaymentFrequency", props.selectedFinanceQuote.financeLoanClassId);
  const selectedDeferredDownPaymentFrequency = getEnumForValue(paymentFrequencies, props.selectedFinanceQuote.deferredDownPaymentPaymentFrequency);
  const errorMessages = getFinanceQuoteErrorMessages(props.applicationFinanceErrors);  
  
  return (
    <div className={styles.financeDeferredDownEditorContainer}>
      {quoteWasCreatedAfterCutoverDate &&
        <Tooltip
          title={"This checkbox ties the loan's first payment date to the last deferred down payment date. Unchecking this will let the changing of the two dates not affect the other."}
          placement={'bottom-start'}
          arrow
        >
          <FormControlLabel
            className={styles.deferredDownCheckbox}
            label={'Keep first payment date and deferred down payment dates in sync'}
            control={
              <Checkbox // Using a checkbox for now, but it can easily be switched to a dropdown
                checked={props.selectedFinanceQuote.tieDeferredDownPaymentsToFirstPaymentDate}
                onChange={() => props.onChangeSingleField(
                  'tieDeferredDownPaymentsToFirstPaymentDate',
                  !props.selectedFinanceQuote.tieDeferredDownPaymentsToFirstPaymentDate,
                  props.selectedFinanceQuote.id
                )}
              />
            }
          />
        </Tooltip>
      }
      <Card className={props.className}>
        <CardHeader>
          <CardTitle>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <span>Deferred Down</span>
              {!((quoteWasCreatedAfterCutoverDate && deferredPayments.length > 0) || props.financeQuoteFieldIsDisabled("deferredPayments") || deferredPayments.length >= maxNumberOfPayments) && (
                <IconButton
                  size='small'
                  title={'Add Deferred Payment'}
                  style={{ color: '#757575' }}
                  onClick={() => addOrEditDeferredPayment(undefined)}
                >
                  <AddIcon fontSize='small' />
                </IconButton>
              )}

            </div>
          </CardTitle>
        </CardHeader>
        <div className={styles.sectionItem}>
          <ComboBox
            disabled={props.financeQuoteFieldIsDisabled("deferredDownPaymentPaymentFrequency")}
            valid={!hasValue(errorMessages.deferredDownPaymentPaymentFrequency)}
            label={'Deferred Down Pay Frequency'}
            data={paymentFrequencies}
            textField="option"
            suggest={true}
            clearButton={false}
            value={selectedDeferredDownPaymentFrequency}
            onChange={(event: ComboBoxChangeEvent) => props.onChangeSingleField(
              'deferredDownPaymentPaymentFrequency',
              event.value.value,
              props.selectedFinanceQuote.id
              )}
          />
          <QuoterError message={errorMessages.deferredDownPaymentPaymentFrequency}/>
        </div>
        <div className={styles.sectionItem}>
          <GenericGrid<FinanceQuoteDeferredPayment>
            style={{ display: 'flex', flex: 1 }}
            size={'small'}
  
            useIconButtons={true}
            columns={[
              { title: "Date", field: "paymentDate", width: 100, format: "{0:d}" },
              { title: "Amount", field: "amount", format: "{0:c}" }
            ]}
            itemName={' '}
            itemNameStyle={{ fontWeight: 'bold', fontSize: '1.2em' }}
            items={deferredPayments.map(payment => { return { ...payment, paymentDate: new Date(payment.paymentDate!) } })}
            disableAdding={true}
            disableEditing={props.financeQuoteFieldIsDisabled("deferredPayments") || deferredPayments.length > maxNumberOfPayments}
            disableExcelExporting={true}
            disablePdfExporting={true}
            disableFiltering={true}
            disableGrouping={true}
            onOpenItemEditor={(deferredPayment: FinanceQuoteDeferredPayment | undefined) => addOrEditDeferredPayment(deferredPayment)}
            onItemSelected={(deferredPayment: FinanceQuoteDeferredPayment) => addOrEditDeferredPayment(deferredPayment)}
            disablePaging={deferredPayments.length <= 20}
          />
        </div>
      </Card>
    </div>
  );
}
